import { z } from 'zod'
import { zodValidator } from '@tanstack/zod-adapter'
import type { DateRange } from 'react-day-picker'
import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/shared/page-header'
import { SectionCard } from '@/components/shared/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import { AlertCircle, Info, X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { useDashboard, useCampaigns } from '@/hooks'
import type { DashboardData, RecentReferral } from '@/lib/api'
import { RankingList } from '@/components/shared/ranking-list'

import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const searchSchema = z.object({
  campaignId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
})

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
  validateSearch: zodValidator(searchSchema),
})

type SmartCardConfig = {
  key: keyof DashboardData
  title: string
  format: (value: number) => string
  kpiClass: string
  secondaryMetric?: {
    label: string
    value: (data: DashboardData) => string
    tooltip: string
  }
}

const SMART_CARDS: SmartCardConfig[] = [
  {
    key: 'totalMembers',
    title: 'Membros',
    format: (v) => v.toLocaleString('pt-BR'),
    kpiClass: 'text-kpi',
  },
  {
    key: 'validReferrals',
    title: 'Indicações Válidas',
    format: (v) => v.toLocaleString('pt-BR'),
    kpiClass: 'text-kpi',
  },
  {
    key: 'totalRewardsPaid',
    title: 'Investimento',
    format: (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    kpiClass: 'text-kpi-md',
    secondaryMetric: {
      label: 'Custo %',
      value: (data) => `${data.costEfficiency}%`,
      tooltip: 'Porcentagem da receita gasta em recompensas (Eficiência do Canal)',
    },
  },
  {
    key: 'totalRevenue',
    title: 'Receita Gerada',
    format: (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    kpiClass: 'text-kpi-md',
    secondaryMetric: {
      label: 'ROI',
      value: (data) => `${data.roi}x`,
      tooltip: 'Retorno sobre Investimento: Receita Total / Total Investido',
    },
  },
]

const STATUS_CONFIG: Record<
  RecentReferral['status'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; dotClass: string }
> = {
  pending: { label: 'Pendente', variant: 'secondary', dotClass: 'bg-amber-400' },
  approved: { label: 'Aprovada', variant: 'default', dotClass: 'bg-primary' },
  cancelled: { label: 'Cancelada', variant: 'destructive', dotClass: 'bg-destructive' },
  rejected: { label: 'Rejeitada', variant: 'destructive', dotClass: 'bg-destructive' },
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="space-y-0 pb-1">
        <Skeleton className="h-3 w-24" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-10 w-24 mb-1" />
        <Skeleton className="h-3 w-16" />
      </CardContent>
    </Card>
  )
}

function RecentReferralsList({ referrals }: { referrals: RecentReferral[] }) {
  if (referrals.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">Nenhuma indicação recente</p>
    )
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-1.25 top-3 bottom-3 w-px bg-border" />

      <div className="space-y-0">
        {referrals.map((referral) => {
          const status = STATUS_CONFIG[referral.status]
          return (
            <div key={referral.id} className="relative flex items-start gap-3 py-2.5">
              {/* Timeline dot */}
              <div
                className={`relative z-10 mt-1.5 w-2.75 h-2.75 rounded-full shrink-0 ring-2 ring-card ${status.dotClass}`}
              />

              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium truncate">{referral.refereeName}</span>
                    <Badge variant={status.variant} className="shrink-0 text-[10px] px-1.5 py-0">
                      {status.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    por {referral.referrerName || 'Desconhecido'}
                    {referral.createdAt && (
                      <span className="ml-1">
                        ·{' '}
                        {formatDistanceToNow(new Date(referral.createdAt), {
                          addSuffix: false,
                          locale: ptBR,
                        })}
                      </span>
                    )}
                  </p>
                </div>
                <p className="text-sm font-medium tabular-nums shrink-0">
                  {referral.orderAmount.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function RankingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-7 w-7 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
}

function RecentReferralsSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div
          key={i}
          className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
        >
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-14 rounded-sm" />
            </div>
            <Skeleton className="h-3 w-36" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
}

function DashboardPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const selectedCampaign = search.campaignId ?? 'all'
  const dateRange: DateRange | undefined =
    search.startDate && search.endDate
      ? { from: new Date(search.startDate), to: new Date(search.endDate) }
      : undefined

  const filters = {
    startDate: search.startDate,
    endDate: search.endDate,
    campaignId: search.campaignId,
  }

  const handleCampaignChange = (val: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        campaignId: val === 'all' ? undefined : val,
      }),
    })
  }

  const handleDateChange = (range: DateRange | undefined) => {
    navigate({
      search: (prev) => ({
        ...prev,
        startDate: range?.from?.toISOString(),
        endDate: range?.to?.toISOString(),
      }),
    })
  }

  const hasActiveFilters = selectedCampaign !== 'all' || !!search.startDate || !!search.endDate

  const handleClearFilters = () => {
    navigate({ search: {} })
  }

  const { data, isLoading, error } = useDashboard(filters)
  const { data: campaigns } = useCampaigns()

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-muted-foreground">Erro ao carregar dashboard</p>
          <p className="text-sm text-destructive">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Visão geral do seu programa de indicações"
        actions={
          <div className="flex w-full flex-col sm:w-auto sm:flex-row sm:items-center gap-2">
            {/* Mobile: filtros lado a lado, full-width */}
            <div className="sm:hidden space-y-2">
              <div className="flex gap-2">
                <Select value={selectedCampaign} onValueChange={handleCampaignChange}>
                  <SelectTrigger className="flex-1 min-w-0">
                    <SelectValue placeholder="Campanhas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Campanhas</SelectItem>
                    {campaigns?.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DatePickerWithRange date={dateRange} setDate={handleDateChange} />
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-3 w-3" />
                  Limpar filtros
                </button>
              )}
            </div>
            {/* Desktop: linha simples */}
            <div className="hidden sm:flex items-center gap-2">
              <Select value={selectedCampaign} onValueChange={handleCampaignChange}>
                <SelectTrigger className="w-50">
                  <SelectValue placeholder="Todas as Campanhas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Campanhas</SelectItem>
                  {campaigns?.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DatePickerWithRange date={dateRange} setDate={handleDateChange} align="end" />
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                  Limpar filtros
                </Button>
              )}
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {isLoading
          ? SMART_CARDS.map((stat) => <StatCardSkeleton key={stat.key} />)
          : SMART_CARDS.map((stat) => (
              <Card key={stat.key}>
                <CardHeader className="space-y-0 pb-1">
                  <CardTitle className="text-[11px]! font-semibold! uppercase tracking-wider text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={stat.kpiClass}>
                    {data ? stat.format(data[stat.key] as number) : '-'}
                  </div>
                  {stat.secondaryMetric && data && (
                    <div className="flex items-center gap-1 mt-2">
                      <p className="text-xs text-muted-foreground">
                        {stat.secondaryMetric.label}:{' '}
                        <span className="font-semibold text-primary">
                          {stat.secondaryMetric.value(data)}
                        </span>
                      </p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-muted-foreground/50" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{stat.secondaryMetric.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SectionCard title="Ranking de Indicadores">
          {isLoading ? (
            <RankingSkeleton />
          ) : (
            <RankingList
              items={(data?.topReferrers ?? []).map((r) => ({
                id: r.id,
                name: r.name,
                subtitle: `${r.referrals} ${r.referrals === 1 ? 'indicação' : 'indicações'}`,
                value: r.totalRevenue,
              }))}
              formatValue={(v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              emptyMessage="Nenhuma indicação ainda"
            />
          )}
        </SectionCard>

        <SectionCard title="Últimas Indicações">
          {isLoading ? (
            <RecentReferralsSkeleton />
          ) : (
            <RecentReferralsList referrals={data?.recentReferrals ?? []} />
          )}
        </SectionCard>
      </div>
    </div>
  )
}
